import { useMemo, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router'

import CardActionArea from '@mui/material/CardActionArea'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@components/_mui/Typography'

import { Indicator, invalidateIndicator } from '@shared/hooks/src/useIndicator'
import usePubNub, { Channels } from '@shared/hooks/src/usePubNub'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { queryClient, setQueriesData } from '@shared/providers/src/QueryClientProvider'
import { filterCache, handleErrorSilently, NotificationSetting, QK, UserRole, UserSettingName } from '@shared/utils'

import useSound from '@hooks/useSound'
import Avatar from '@components/Avatar'

import { useGetPatient } from '../Appointments/Appointments.hooks'
import { styles, Title } from '../Notifications.utils'

/**
 * Handle the display of an On Demand Appointment notification.
 */
export default function OnDemandAppointments() {
  const me = useMe()
  const notificationSettings = useUserSetting(me, UserSettingName.NotificationSettings)

  const [open, setOpen] = useState(false)
  const [patient, setPatient] = useState(undefined)
  const [message, setMessage] = useState(undefined)

  const currentPath = useLocation().pathname
  const isVisitPage = currentPath.startsWith('/app/visits')
  const isProvider = me.role === UserRole.Provider

  const enabled = useMemo(() => {
    if (isProvider && isVisitPage) return notificationSettings.value[NotificationSetting.OnDemandAppointmentsDuringVisit]
    return true
  }, [isProvider, isVisitPage, notificationSettings.value])

  const handleClose = () => setOpen(false)

  const getPatient = useGetPatient()
  const playSound = useSound({ howlProps: { src: '/sounds/notification3.mp3' } })

  const handlePendingOnDemand = ({ patient_id: patientId, minutes }) => {
    invalidateOnDemands()

    getPatient
      .mutateAsync(patientId)
      .then((patient) => {
        setMessage(getMessage(minutes))
        setPatient(patient)
        setOpen(true)
        playSound()
      })
      .catch(handleErrorSilently)
  }

  usePubNub(
    'platform_admins',
    ({ action, attributes }) => {
      if ([Channels.OnDemandPending, Channels.OnDemandStatusUpdate].includes(action)) {
        invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'accessible' })
      }
      if (Channels.OnDemandPending === action) {
        handlePendingOnDemand(attributes)
      }
      if (Channels.OnDemandStatusUpdate === action) {
        filterOnDemands(attributes.on_demand_id)
        invalidatedAppointments()
      }
    },
    {
      enabled: me.role === UserRole.Admin,
    }
  )

  usePubNub(
    `user_${me.id}`,
    ({ action, attributes }) => {
      if (Channels.OnDemandPending === action) {
        handlePendingOnDemand(attributes)
      }
    },
    {
      enabled: me.role === UserRole.Provider && enabled,
    }
  )

  usePubNub('on_demand_appointments', async ({ action, attributes }) => {
    const { on_demand_id } = attributes

    if (me.role === UserRole.Provider) {
      invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'provider_queue' })
      invalidateIndicator(me.id, Indicator.HasOnDemandOrAsyncMessaging, { scope: 'accessible' })
    }

    if (Channels.OnDemandPending === action) return invalidateOnDemands()
    if ([Channels.OnDemandAbandoned, Channels.OnDemandRejected].includes(action)) return filterOnDemands(on_demand_id)
    if (Channels.OnDemandAccepted === action) {
      if (me.role === UserRole.Admin) {
        invalidateOnDemand(on_demand_id)
        invalidateOnDemands()
        return
      }
      return filterOnDemands(on_demand_id)
    }
  })

  return (
    <Snackbar
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') handleClose()
      }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <CardActionArea component={RouterLink} to="/app/appointments?tab=on-demand" target="_blank" sx={[styles.container, styles.warning]}>
        <Title label="On Demand Appointments" onClose={handleClose} />
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Avatar user={patient} size="sm" />
          <Typography>
            <b>{patient?.fullName}</b> {message}
          </Typography>
        </Stack>
      </CardActionArea>
    </Snackbar>
  )
}

const invalidateOnDemand = (id) => queryClient.invalidateQueries({ queryKey: QK.appointments.onDemand.id(id).details })
const invalidateOnDemands = () => queryClient.invalidateQueries({ queryKey: QK.appointments.onDemand.lists })
const invalidatedAppointments = () => queryClient.invalidateQueries({ queryKey: QK.appointments.lists })
const filterOnDemands = (id) =>
  setQueriesData(
    { queryKey: QK.appointments.onDemand.lists },
    filterCache((onDemandLog) => onDemandLog.id !== id)
  )

const getMessage = (minutes) => {
  if (!minutes) return 'is waiting to connect for an on demand appointment.'

  switch (minutes) {
    case 0:
      return 'has just requested an on demand appointment.'
    case 3:
      return 'has been waiting for an on demand appointment for 3 minutes.'
    case 6:
      return 'has been waiting for an on demand appointment for 6 minutes. Please respond soon.'
    case 9:
      return 'has been waiting for an on demand appointment for 9 minutes. This is the final reminder — the request will be canceled in 3 minutes.'
    default:
      return `has been waiting for an on demand appointment for ${minutes} minutes.`
  }
}
